import { geo } from '../../core/geo';
import { button, createScreen, createSheet, h } from './common';

export function isIos(ua: string = navigator.userAgent, touchPoints: number = navigator.maxTouchPoints): boolean {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
}

/** ㉓c の手順（句点なし）。メニュー名は要実機確認 */
export function deniedSteps(ios: boolean): string[] {
  return ios
    ? [
        '画面の下のアドレス欄を出す',
        'アドレス欄の左端にある小さな文字のボタン（「ぁあ」か「AA」）を押す',
        '「Webサイトの設定」を押す',
        '「位置情報」を「許可」にする',
        '下の「読み込み直す」を押す',
      ]
    : [
        '画面の上のアドレス欄を出す',
        'アドレス欄の左端にあるマーク（2本の線に丸、鍵のこともあります）を押す',
        '「権限」を押す',
        '「位置情報」をオンにする',
        '下の「読み込み直す」を押す',
      ];
}

const HONEY_UNDERLINE = 'text-decoration:underline;text-decoration-color:var(--honey);text-decoration-thickness:3px;text-underline-offset:4px';
const SYS = "-apple-system,'Hiragino Sans','Noto Sans JP',Roboto,sans-serif";

/** 端末の確認画面の見本（この部分だけ端末の見た目）。押すボタンを真鍮色の線の楕円で囲む */
function sampleDialog(ios: boolean): HTMLElement {
  const box = h('div');
  box.style.cssText = `margin:18px auto 14px;width:100%;max-width:290px;font-family:${SYS};color:#111`;
  const oval = (): HTMLElement => {
    const o = h('span');
    o.style.cssText =
      'position:absolute;inset:-3px -10px;border:2px solid var(--brass);border-radius:50%;pointer-events:none';
    return o;
  };
  if (ios) {
    box.style.cssText += ';background:#f2f2f2;border-radius:14px;overflow:hidden;text-align:center';
    const msg = h('div', undefined, '“Waggle”が現在の位置情報の使用を求めています');
    msg.style.cssText = 'padding:18px 16px 16px;font-size:15px;font-weight:600;line-height:1.4';
    const row = h('div');
    row.style.cssText = 'display:flex;border-top:1px solid #c6c6c8;font-size:17px;color:#007aff';
    const no = h('div', undefined, '許可しない');
    no.style.cssText = 'flex:1;padding:12px 0;border-right:1px solid #c6c6c8';
    const yes = h('div');
    yes.style.cssText = 'flex:1;padding:12px 0;font-weight:600;position:relative';
    const yl = h('span', undefined, '許可');
    yl.style.cssText = 'position:relative;display:inline-block';
    yl.appendChild(oval());
    yes.appendChild(yl);
    row.append(no, yes);
    box.append(msg, row);
  } else {
    box.style.cssText += ';background:#fff;border-radius:24px;padding:20px 20px 12px;box-shadow:0 2px 10px rgba(0,0,0,.3)';
    const msg = h('div', undefined, 'Waggle に現在地の使用を許可しますか？');
    msg.style.cssText = 'font-size:16px;line-height:1.5;margin:0 0 12px';
    box.appendChild(msg);
    ['訪問時に許可', '今回のみ許可', '許可しない'].forEach((t, i) => {
      const r = h('div');
      r.style.cssText = 'padding:11px 0;font-size:15px;font-weight:600;color:#0b57d0;text-align:center';
      const l = h('span', undefined, t);
      l.style.cssText = 'position:relative;display:inline-block';
      if (i === 0) l.appendChild(oval());
      r.appendChild(l);
      box.appendChild(r);
    });
  }
  return box;
}

function ask(root: HTMLElement): { answer: () => Promise<boolean>; showNo: () => void; remove: () => void } {
  const screen = createScreen(root);
  const { wrap, sheet } = createSheet(1201);
  screen.appendChild(wrap);
  sheet.appendChild(h('h1', undefined, '位置情報を使用します'));
  const p = h('p');
  p.style.cssText = 'font-size:17px;font-weight:700;line-height:1.6;margin:0';
  const key = h('span', undefined, '許可');
  key.style.cssText = HONEY_UNDERLINE;
  p.append(document.createTextNode('次に出る確認で、'), h('br'), document.createTextNode('「'), key, document.createTextNode('」を押してください'));
  sheet.appendChild(p);
  sheet.appendChild(sampleDialog(isIos()));
  const no = h('p');
  no.style.cssText = 'margin:0;text-align:center;font-size:22px;font-weight:900;line-height:1.5;color:var(--red)';
  sheet.appendChild(no);

  let pending: ((v: boolean) => void) | null = null;
  const row = h('div', 'sh-btnrow');
  row.append(
    button('はい', 'ink', () => pending?.(true)),
    button('いいえ', 'paper', () => pending?.(false)),
  );
  screen.appendChild(row);
  return {
    answer: () => new Promise<boolean>((resolve) => (pending = resolve)),
    showNo: () => no.replaceChildren(document.createTextNode('このゲームは'), h('br'), document.createTextNode('GPSが必須です')),
    remove: () => screen.remove(),
  };
}

function denied(root: HTMLElement): Promise<'granted'> {
  const screen = createScreen(root);
  const { wrap, sheet } = createSheet(1301);
  screen.appendChild(wrap);
  const t = h('h1');
  t.append(document.createTextNode('位置情報が'), h('br'), document.createTextNode('許可されていません'));
  sheet.appendChild(t);
  sheet.appendChild(h('p', undefined, '次の手順で許可し、読み込み直してください'));
  const ol = h('ol');
  ol.style.cssText = 'margin:0 0 12px;padding-left:1.5em;font-size:15px;line-height:1.7';
  for (const s of deniedSteps(isIos())) ol.appendChild(h('li', undefined, s));
  sheet.appendChild(ol);
  sheet.appendChild(h('p', undefined, 'それでも直らないときは、端末の設定で位置情報サービスがオンになっているか確かめてください。'));
  screen.appendChild(button('読み込み直す', 'ink', () => location.reload()));

  // 読み込み直さなくても許可に変わったら先へ進む
  return new Promise<'granted'>((resolve) => {
    void navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((st) => {
        st.onchange = () => {
          if (st.state === 'granted') {
            void geo.start().then(() => {
              screen.remove();
              resolve('granted');
            });
          }
        };
      })
      .catch(() => undefined);
  });
}

/**
 * 位置情報の許可を取る（㉓a・㉓b・㉓c）。許可済みなら画面を出さずに開始する。
 * 拒否されたときは ㉓c を出したまま待つ（このゲームは GPS が必須。読み込み直すか、許可に変わると先へ進む）。
 * 戻り値の 'denied' は、位置情報が使えない端末（geolocation なし）でのみ返る。
 */
export async function ensureLocation(root: HTMLElement): Promise<'granted' | 'denied'> {
  const perm = await geo.permission();
  if (perm === 'unsupported') return 'denied';
  if (perm === 'granted') {
    await geo.start();
    return 'granted';
  }
  if (perm === 'denied') return denied(root);
  const a = ask(root);
  while (!(await a.answer())) a.showNo();
  a.remove();
  const st = await geo.start();
  return st === 'denied' ? denied(root) : 'granted';
}
